<template>
  <q-btn-dropdown v-if="isEnabled"
                  :color="color"
                  :disable="disable"
                  :loading="loading"
                  :icon="icon"
                  :flat="flat"
                  :round="round"
                  :dense="dense"
                  :fab="fab"
                  :unelevated="unelevated"
                  :stretch="stretch"
                  :size="size"
                  :no-wrap="noWrap"
                  :text-color="textColor"
                  :title="title"
                  :style="style"
                  :menu-anchor="menuAnchor"
                  :menu-self="menuSelf"
                  :menu-offset="menuOffset"
                  :auto-close="autoClose"
                  :split="split"
                  :dropdown-icon="dropdownIcon"
                  :content-class="contentClass"
                  :content-style="contentStyle"
                  :cover="cover"
                  :persistent="persistent"
                  v-bind="$attrs"
  >
    <template #label>
      <slot name="label">
        {{ label }}
      </slot>
    </template>
    <slot />
  </q-btn-dropdown>
  <CuiButtonDropdown v-else
                     :disabled="disable"
                     :loading="loading"
                     :size="size"
                     :aria-label="title || label"
                     :style="style"
                     variant="secondary-outline"
                     :icon="normalizedIcon"
                     :split="split"
  >
    <template #label>
      <slot name="label">
        {{ label }}
      </slot>

    </template>
    <template #default>
      <slot />
    </template>
  </CuiButtonDropdown>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useFeatureFlags } from "@/composables/useFeatureFlags";

const { isEnabled: checkEnabled } = useFeatureFlags();
const isEnabled = checkEnabled("dtpButtonDropdown");

type MenuAnchor =
  | "top right"
  | "top left"
  | "top middle"
  | "top start"
  | "top end"
  | "center left"
  | "center middle"
  | "center right"
  | "center start"
  | "center end"
  | "bottom left"
  | "bottom middle"
  | "bottom right"
  | "bottom start"
  | "bottom end";

type Props = {
  color?: string;
  disable?: boolean;
  loading?: boolean;
  icon?: string;
  label?: string;
  flat?: boolean;
  round?: boolean;
  dense?: boolean;
  fab?: boolean;
  unelevated?: boolean;
  stretch?: boolean;
  size?: string;
  noWrap?: boolean;
  textColor?: string;
  title?: string;
  style?: string | Record<string, any>;
  menuAnchor?: MenuAnchor;
  menuSelf?: MenuAnchor;
  menuOffset?: number[];
  autoClose?: boolean;
  split?: boolean;
  dropdownIcon?: string;
  contentClass?: string;
  contentStyle?: string | Record<string, any>;
  cover?: boolean;
  persistent?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  color: undefined,
  disable: false,
  loading: false,
  icon: undefined,
  label: undefined,
  flat: false,
  round: false,
  dense: false,
  fab: false,
  unelevated: false,
  stretch: false,
  size: undefined,
  noWrap: false,
  textColor: undefined,
  title: undefined,
  style: undefined,
  menuAnchor: undefined,
  menuSelf: undefined,
  menuOffset: undefined,
  autoClose: undefined,
  split: false,
  dropdownIcon: undefined,
  contentClass: undefined,
  contentStyle: undefined,
  cover: false,
  persistent: false
});

const normalizedIcon = computed(() => {
  if (!props.icon) return undefined;
  // Remove mdi- prefix if present
  const iconName = props.icon.startsWith("mdi-") ? props.icon.slice(4) : props.icon;

  // Map specific icon names to Material Symbols equivalents
  const iconMap: Record<string, string> = {
    "filter": "filter_list",
    "menu-down": "arrow_drop_down",
    "chevron-down": "expand_more"
  };

  return iconMap[iconName] || iconName;
});
</script>
